// frontend/src/pages/PricingPage.js
import React, { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { Check, X, Zap, Crown, Shield, Monitor, ChevronDown, ShieldCheck } from 'lucide-react';
import { load } from '@cashfreepayments/cashfree-js'; 
import { BACKEND_URL, supabase } from '../supabaseClient'; 
import { Turnstile } from '@marsidev/react-turnstile'; 
import './PricingPage.css';

const PricingPage = () => {
  const navigate = useNavigate();
  
  const [billingCycle, setBillingCycle] = useState('yearly');
  const [currencySymbol, setCurrencySymbol] = useState('$');
  const [plans, setPlans] = useState(null);
  const [loading, setLoading] = useState(true);
  const [cashfree, setCashfree] = useState(null);

  const [showSecurityModal, setShowSecurityModal] = useState(false);
  const [pendingPlan, setPendingPlan] = useState(null); 

  useEffect(() => {
    const initCashfree = async () => {
      try {
        const cf = await load({ mode: "sandbox" });
        setCashfree(cf);
      } catch (err) {
        console.error("Cashfree SDK Failed to Load", err);
      }
    };
    initCashfree();
  }, []);

  useEffect(() => {
    const fetchPricing = async () => {
      try {
        const response = await fetch(`${BACKEND_URL}/api/pricing`);
        if (!response.ok) throw new Error('Failed to load pricing');
        const data = await response.json();
        setCurrencySymbol(data.meta.symbol);
        setPlans(data.plans);
      } catch (err) {
        console.error("Pricing Fetch Error:", err);
      } finally {
        setLoading(false);
      }
    };
    fetchPricing();
  }, []);

  const handlePlanClick = async (planId, price) => {
    if (price === 0) return navigate('/app/apparel');
    if (planId === "agency") return navigate('/contact-us');

    const { data: { session } } = await supabase.auth.getSession();
    if (!session) {
      return navigate('/login?redirect=/pricing');
    }

    if (!cashfree) {
      alert("Payment system is initializing... please try again.");
      return;
    }

    setPendingPlan({ planId, price });
    setShowSecurityModal(true);
  };

  const handleSecuritySuccess = async (token) => {
    setShowSecurityModal(false);
    if (pendingPlan) {
        await initiatePayment(pendingPlan.planId, token);
    }
  };

  const initiatePayment = async (planId, turnstileToken) => {
    try {
        const { data: { session } } = await supabase.auth.getSession();
        
        const response = await fetch(`${BACKEND_URL}/api/payment/create-order`, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
                'Authorization': `Bearer ${session.access_token}`
            },
            body: JSON.stringify({ 
                planId: planId,
                billingCycle: billingCycle,
                turnstileToken: turnstileToken
            })
        });

        const data = await response.json();

        if (!response.ok) {
            throw new Error(data.error || "Order creation failed");
        }

        cashfree.checkout({
            paymentSessionId: data.payment_session_id,
            returnUrl: `${window.location.origin}/account/billing?status=success`
        });

    } catch (err) {
        console.error("Payment Error:", err);
        alert(`Payment Error: ${err.message}`);
    }
  };

  const getPriceDisplay = (basePrice) => {
    if (basePrice === 0) return { display: 0, original: null, asterisk: null };
    let display = basePrice;
    let original = null;
    let asterisk = null;

    if (billingCycle === 'quarterly') {
      display = (basePrice * 0.90).toFixed(2);
      original = basePrice;
      asterisk = '*';
    } else if (billingCycle === 'yearly') {
      display = (basePrice * 0.75).toFixed(2);
      original = basePrice;
      asterisk = '*';
    }
    return { display, original, asterisk };
  };

  if (loading) return (
    <div className="pricing-container" style={{ display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
       <div className="loader-spinner-small" style={{borderTopColor: 'var(--color-primary)'}}></div>
    </div>
  );

  if (!plans) return <div className="pricing-container">Unable to load plans. Please refresh.</div>;

  const pStarter = getPriceDisplay(plans.starter.price);
  const pLite = getPriceDisplay(plans.lite.price);
  const pPro = getPriceDisplay(plans.pro.price);
  const pAgency = getPriceDisplay(plans.agency.price);

  return (
    <div className="pricing-container">
      
      {/* HEADER SECTION */}
      <div className="pricing-header">
        <h2 className="pricing-subtitle">Pricing</h2>
        
        {/* Updated Title Structure */}
        <div className="pricing-title-block">
            <span className="title-main">Zero Extra Costs.</span>
            <span className="title-sub highlight-red">Pure Physics.</span>
        </div>

        {/* Updated Description with Line Break */}
        <p className="pricing-desc">
          Choose the plan that fits your scale.
          <br />
          All plans include the v25 Warp Engine.
        </p>
        
        <div className="billing-toggle">
          <div className="toggle-bg">
            <button onClick={() => setBillingCycle('monthly')} className={`toggle-btn ${billingCycle === 'monthly' ? 'active' : ''}`}>Monthly</button>
            <button onClick={() => setBillingCycle('quarterly')} className={`toggle-btn ${billingCycle === 'quarterly' ? 'active' : ''}`}>Quarterly <span className="discount-tag">-10%</span></button>
            <button onClick={() => setBillingCycle('yearly')} className={`toggle-btn ${billingCycle === 'yearly' ? 'active' : ''}`}>Yearly <span className="discount-tag">-25%</span></button>
          </div>
        </div>
      </div>

      {/* PRICING GRID */}
      <div className="pricing-grid">
        <PricingCard 
          title="Starter "
          price={plans.starter.price}
          currencySymbol={currencySymbol}
          billingCycle={billingCycle} 
          description="For Trial"
          icon={<Shield size={24} className="icon-check" />}
          features={[
            { text: `${plans.starter.limits.count} Generations / Day`, included: true },
            { text: "720p Resolution", included: true },
            { text: "Watermark", included: true },
            { text: "JPG Only", included: true },
            { text: "No Storage Slots", included: false }, 
            { text: "AI Tools", included: false },         
            { text: "Chatbot Support", included: true }           
          ]}
          buttonText="Start Free"
          onButtonClick={() => handlePlanClick("starter", plans.starter.price)}
        />

        <PricingCard 
          title="Lite"
          price={plans.lite.price}
          currencySymbol={currencySymbol}
          billingCycle={billingCycle}
          description="For hobbyists & new sellers."
          icon={<Zap size={24} className="icon-check" />}
          features={[
            { text: `${plans.lite.limits.count} Generations / Month`, included: true },
            { text: "1080p HD Quality", included: true },
            { text: "No Watermark", included: true },
            { text: "JPG Only", included: true },
            { text: "10 Storage Slots", included: true },
            { text: "100 AI Tools credits / Month", included: true },
            { text: "Chatbot & Email Support", included: true }
          ]}
          buttonText="Get Lite"
          onButtonClick={() => handlePlanClick("lite", plans.lite.price)}
        />

        <PricingCard 
          title="Pro"
          price={plans.pro.price}
          currencySymbol={currencySymbol}
          billingCycle={billingCycle}
          description="For growing POD brands."
          icon={<Crown size={24} className="icon-check" />}
          features={[
            { text: "Unlimited Generations", included: true },
            { text: "1920p FHD Quality", included: true },
            { text: "No Watermark", included: true },
            { text: "JPG, PNG, WebP", included: true },
            { text: "Unlimited AI Tools", included: true },
            { text: "100 Storage Slots", included: true },
            { text: "1 Brand Kit", included: true },
            { text: "Dedicated Support", included: true }
          ]}
          buttonText="Go Pro"
          highlight={true}
          badge="Most Popular"
          onButtonClick={() => handlePlanClick("pro", plans.pro.price)}
        />

        <PricingCard 
          title="Agency"
          price={plans.agency.price}
          currencySymbol={currencySymbol}
          billingCycle={billingCycle}
          description="For volume & power users."
          icon={<Monitor size={24} className="icon-check" />}
          features={[
            { text: "Unlimited Generations", included: true },
            { text: "2560p QHD Quality", included: true },
            { text: "JPG, PNG, WebP, GIF", included: true },
            { text: "Bulk Processing (Batch)", included: true },
            { text: "Unlimited AI Tools", included: true },
            { text: "5,000 Storage Slots", included: true },
            { text: "Unlimited Brand Kits", included: true },
            { text: "Dedicated Support", included: true }
          ]}
          buttonText="Contact Us"
          onButtonClick={() => handlePlanClick("agency", plans.agency.price)}
        />
      </div>

      {/* --- COMPARISON TABLE SECTION --- */}
      <div className="comparison-section">
         
         {/* 1. HEADING WRAPPER (Forced Center) */}
         <div className="comparison-heading-wrapper">
            <h2 className="table-header-title">Compare Features</h2>
            <p className="table-header-subtitle">
               Detailed breakdown of features, limits, and capabilities.
            </p>
         </div>

         {/* 2. TABLE WRAPPER (Scrollable) */}
         <div className="table-wrapper">
            <table className="pricing-table">
               <thead><tr><th>Feature</th><th>Starter </th><th>Lite</th><th>Pro</th><th>Agency</th></tr></thead>
               <tbody>
                  <tr><td><strong>Monthly Price</strong></td><td><div className="table-price">{currencySymbol}{pStarter.display}</div></td><td><div className="table-price">{pLite.original && <span className="original-price">{currencySymbol}{pLite.original}</span>}<span>{currencySymbol}{pLite.display}{pLite.asterisk && <span className="price-asterisk">{pLite.asterisk}</span>}</span></div></td><td><div className="table-price">{pPro.original && <span className="original-price">{currencySymbol}{pPro.original}</span>}<span>{currencySymbol}{pPro.display}{pPro.asterisk && <span className="price-asterisk">{pPro.asterisk}</span>}</span></div></td><td><div className="table-price">{pAgency.original && <span className="original-price">{currencySymbol}{pAgency.original}</span>}<span>{currencySymbol}{pAgency.display}{pAgency.asterisk && <span className="price-asterisk">{pAgency.asterisk}</span>}</span></div></td></tr>
                  <tr><td>Generation Credits</td><td>3 per Day</td><td>{plans.lite.limits.count} per Month</td><td>Unlimited</td><td>Unlimited</td></tr>
                  <tr><td>Max Resolution</td><td>720p</td><td>1080p</td><td>1920p</td><td>2k</td></tr>
                  <tr><td>File Formats</td><td>JPG</td><td>JPG</td><td>JPG, PNG, WebP</td><td>JPG, PNG, WebP, GIF</td></tr>
                  <tr><td>Watermark</td><td><Check className="icon-check" /></td><td><X className="icon-cross" /></td><td><X className="icon-cross" /></td><td><X className="icon-cross" /></td></tr>
                  <tr><td>Storage Slots</td><td><X className="icon-cross" /></td><td>10</td><td>100</td><td>1000</td></tr>
                  <tr><td>Warp Engine</td><td>v25 Basic</td><td>v25 Basic</td><td>v25 + 3D Pop</td><td>v25 + 3D Pop</td></tr>
                  <tr><td>AI Tools Credits</td><td><X className="icon-cross" /></td><td>100 credits/mo</td><td>Unlimited</td><td>Unlimited</td></tr>
                  <tr><td>Brand Kits</td><td><X className="icon-cross" /></td><td><X className="icon-cross" /></td><td>1 Kit</td><td>Unlimited Kits</td></tr>
                  <tr><td>Support</td><td>Chatbot</td><td>Chatbot & Email</td><td>Dedicated Support</td><td>Priority Support</td></tr>
                  <tr><td>Bulk Mode</td><td><X className="icon-cross" /></td><td><X className="icon-cross" /></td><td><X className="icon-cross" /></td><td><Check className="icon-check" /></td></tr>
               </tbody>
            </table>
         </div>
      </div>

      
      <FAQSection />

      {/* SECURITY MODAL */}
      {showSecurityModal && (
        <div className="security-modal-overlay">
            <div className="security-modal-content">
                <button className="security-modal-close" onClick={() => setShowSecurityModal(false)}>
                    <X size={20} />
                </button>
                
                <div className="security-icon-circle">
                    <ShieldCheck size={32} color="#8A181A" />
                </div>
                
                <h3>Security Check</h3>
                <p>Please verify you are human to proceed with your purchase.</p>
                <p className="security-note">Thank you for cooperating with us. This helps enhance the security of FullMockup.</p>

                <div className="turnstile-wrapper">
                    <Turnstile 
                        siteKey={process.env.REACT_APP_TURNSTILE_SITE_KEY} 
                        onSuccess={handleSecuritySuccess}
                    />
                </div>
            </div>
        </div>
      )}

    </div>
  );
};

const PricingCard = ({ title, price, currencySymbol, billingCycle, description, features, buttonText, highlight, badge, icon, onButtonClick }) => {
  let displayPrice = price;
  let showStrike = false;
  let asterisk = null;
  if (price > 0) {
    if (billingCycle === 'quarterly') {
      displayPrice = (price * 0.90).toFixed(2);
      showStrike = true;
      asterisk = "*";
    } else if (billingCycle === 'yearly') {
      displayPrice = (price * 0.75).toFixed(2);
      showStrike = true;
      asterisk = "*";
    }
  }
  if (price === 0) { displayPrice = 0; showStrike = false; asterisk = null; }
  const cycleLabel = price > 0 ? '/mo' : '';

  return (
    <div className={`pricing-card ${highlight ? 'highlighted' : ''}`}>
      {badge && <div className="popular-badge">{badge}</div>}
      <div className="card-header">
        <div className="card-title-row"><h3>{title}</h3>{icon}</div>
        <p className="pricing-desc" style={{ fontSize: '0.9rem', margin: 0, padding: 0 }}>{description}</p>
      </div>
      <div className="card-price">
        {showStrike && <span className="original-price">{currencySymbol}{price}</span>}
        <span>{currencySymbol}{displayPrice}{asterisk && <span className="price-asterisk">{asterisk}</span>}</span>
        <span className="period">{cycleLabel}</span>
      </div>
      <ul className="features-list">
        {features.map((feature, index) => (
          <li key={index} className="feature-item">
            {feature.included ? <Check className="icon-check" /> : <X className="icon-cross" />}
            <span style={{ opacity: feature.included ? 1 : 0.6 }}>{feature.text}</span>
          </li>
        ))}
      </ul>
      {price > 0 && billingCycle === 'yearly' && <span className="billing-note">*Billed yearly (Save 25%)</span>}
      {price > 0 && billingCycle === 'quarterly' && <span className="billing-note">*Billed quarterly (Save 10%)</span>}
      <button className="card-btn" onClick={onButtonClick}>{buttonText}</button>
    </div>
  );
};

const FAQSection = () => {
    const [activeIndex, setActiveIndex] = useState(null);
    useEffect(() => {
        let timer;
        if (activeIndex !== null) timer = setTimeout(() => setActiveIndex(null), 60000);
        return () => clearTimeout(timer);
    }, [activeIndex]);
    const toggleFAQ = (index) => setActiveIndex(activeIndex === index ? null : index);
    const faqData = [
        { question: "What are Generation Credits?", answer: "Generation credits are the 'currency' used to create mockups. Each time you click 'Generate', one credit is used. Free users get 3 credits daily (reset every 24h). Paid users get a monthly allowance." },
        { question: "What are Storage Slots?", answer: "Storage slots allow you to save your generated mockups in the cloud. If you run out of slots, you must delete old images to save new ones. Pro plans offer significantly more slots." },
        { question: "What are AI Tools?", answer: "We offer a suite of AI-powered tools designed to enhance your workflow, such as Background Remover, Upscaler, and Magic Eraser. Pro plans get unlimited access to these tools." },
        { question: "What are Brand Kits?", answer: "Brand Kits allow you to save your brand's unique assets (logos, color palettes) for one-click application to any mockup, saving you time." }
    ];
    return (
        <div className="faq-section">
        <div className="faq-heading-wrapper"><h2 className="faq-title">Have a question?</h2><p className="faq-subtitle">Read some common questions about our plans.</p></div>
        <div className="faq-container">
            {faqData.map((item, index) => (
            <div key={index} className={`faq-item ${activeIndex === index ? 'active' : ''}`}>
                <button className="faq-question" onClick={() => toggleFAQ(index)}>{item.question}<ChevronDown className="faq-icon" /></button>
                <div className="faq-answer"><p>{item.answer}</p></div>
            </div>
            ))}
        </div>
        <div className="faq-more-link"><p>Still have doubts? <Link to="/faq">Visit our full FAQ page</Link></p></div>
        </div>
    );
};

export default PricingPage;